import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { useInitiativeTasks } from '../../../../api/initiative-client/initiative-client.hooks';
import { InitiativeTasksFilters } from '../../../../api/initiative-client/initiative-client.type';
import type { MinifiedTask } from '../../../../api/tasks-client/task-client.type';
import { useWorkPeriodsTasksSingular } from '../../../../api/work-periods-client/work-periods-client.hooks';
import { Measure, TimeAllocationType } from '../../../../api/work-periods-client/work-periods-client.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useRelatedTasksTabStore, useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  setRelatedTasksData,
  setRelatedTasksPage,
  setRelatedTasksSelectedTypes,
} from '../../../../store/strategy-store/strategy-store.actions';
import { collectTaskTypes, filterTasksBySearchTerm } from './related-tasks-tab.helpers';
import { RelatedTasksMode } from './related-tasks-tab.type';

/**
 * Hook that initiates API calls to fetch related tasks data.
 *
 * @returns {Object} Object containing combined loading state
 * @returns {boolean} returns.isLoading - True if either API is still loading
 */
const useRelatedTasksData = (): { isLoading: boolean } => {
  const { isLoading: isLoadingInitiativeTasks } = useInitiativeTasksAPI();
  const { isLoading: isLoadingWorkPeriodsTasks } = useWorkPeriodsTasksAPI();

  return {
    isLoading: isLoadingWorkPeriodsTasks || isLoadingInitiativeTasks,
  };
};

/**
 * Hook that fetches and manages initiative tasks data via the initiative tasks API.
 *
 * @returns {Object} Object containing loading state
 * @returns {boolean} returns.isLoading - True if the API request is in progress
 */
const useInitiativeTasksAPI = (): { isLoading: boolean } => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedDate = useRelatedTasksTabStore((state) => state.selectedDate);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedTeam = useRelatedTasksTabStore((state) => state.selectedTeam);
  const selectedPhase = useRelatedTasksTabStore((state) => state.selectedPhase);
  const relatedTasksData = useRelatedTasksTabStore((state) => state.tasks);

  const filter = useFilterParam();
  const isEnabled = !selectedPhase && Boolean(selectedDate);

  const { data, query } = useInitiativeTasks(
    {
      portfolioId: portfolio?.id,
      initiativeId: initiative?.id,
      filter,
      date: selectedDate ? dayjs(selectedDate).format('YYYY-MM') : null,
      epicId: mode === RelatedTasksMode.ContributingEpics ? selectedEpic : null,
      projectId: mode === RelatedTasksMode.ContributingTeams ? selectedTeam : null,
    },
    {
      enabled: isEnabled,
      staleTime: 0,
      gcTime: 0,
    },
  );

  const taskData = data?.[0]?.tasks ?? [];

  useDeepCompareEffect(() => {
    if (data && !isEqual(taskData, relatedTasksData) && isEnabled) {
      const taskTypes = collectTaskTypes(taskData);

      setRelatedTasksPage(1);
      setRelatedTasksSelectedTypes(taskTypes);
      setRelatedTasksData(taskData);
    }
  }, [data, taskData, relatedTasksData, isEnabled]);

  useEffect(() => {
    if (query.error) {
      setRelatedTasksData([]);
      Sentry.captureException(query.error);
    }
  }, [query.error]);

  return {
    isLoading: query.isFetching,
  };
};

/**
 * Hook that fetches and manages work periods tasks data via the work periods tasks API.
 *
 * @returns {Object} Object containing loading state
 * @returns {boolean} returns.isLoading - True if the API request is in progress
 */
const useWorkPeriodsTasksAPI = (): { isLoading: boolean } => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const selectedDate = useRelatedTasksTabStore((state) => state.selectedDate);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedPhase = useRelatedTasksTabStore((state) => state.selectedPhase);
  const relatedTasksData = useRelatedTasksTabStore((state) => state.tasks);

  const isEnabled = Boolean(selectedPhase);

  const { data, query } = useWorkPeriodsTasksSingular({
    payload: {
      measure_name: [Measure.FlowByPhase],
      initiative_id: !selectedEpic ? initiative?.id : undefined,
      epic_id: selectedEpic ? selectedEpic : undefined,
      start_date: initiative?.effective_start_date
        ? dayjs(initiative?.effective_start_date).format('YYYY-MM-DD')
        : dayjs(initiative?.start_date).format('YYYY-MM-DD'),
      end_date: initiative?.effective_end_date
        ? dayjs(initiative?.effective_end_date).format('YYYY-MM-DD')
        : dayjs(initiative?.end_date).format('YYYY-MM-DD'),
      measure_filter: `${selectedPhase?.toLowerCase()},${dayjs(selectedDate).format('YYYY-MM')}`,
      time_allocation_type: TimeAllocationType.Monthly,
    },
    options: {
      enabled: isEnabled,
      staleTime: 0,
      gcTime: 0,
    },
  });

  useDeepCompareEffect(() => {
    if (data && !isEqual(data, relatedTasksData) && isEnabled) {
      setRelatedTasksPage(1);
      setRelatedTasksData(data);
    }
  }, [data, relatedTasksData, isEnabled]);

  useEffect(() => {
    if (query.error) {
      setRelatedTasksData([]);
      Sentry.captureException(query.error);
    }
  }, [query.error]);

  return {
    isLoading: query.isFetching,
  };
};

/**
 * Hook that determines the appropriate initiative tasks filter based on the current mode and selections
 *
 * @returns {InitiativeTasksFilters} The determined filter value based on current state
 */
const useFilterParam = (): InitiativeTasksFilters => {
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedThroughput = useRelatedTasksTabStore((state) => state.selectedThroughput);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedTeam = useRelatedTasksTabStore((state) => state.selectedTeam);

  const filter = useMemo(() => {
    switch (mode) {
      case RelatedTasksMode.ContributingTasks:
        return selectedThroughput as InitiativeTasksFilters;
      case RelatedTasksMode.ContributingEpics:
        return selectedEpic ? InitiativeTasksFilters.Epic : InitiativeTasksFilters.Total;
      case RelatedTasksMode.ContributingTeams:
        return selectedTeam ? InitiativeTasksFilters.Project : InitiativeTasksFilters.Total;
      default:
        return InitiativeTasksFilters.Total;
    }
  }, [mode, selectedEpic, selectedTeam, selectedThroughput]);

  return filter;
};

/**
 * Hook that filters tasks based on the current mode and selected task types
 *
 * @returns {MinifiedTask[]} Array of filtered tasks.
 */
const useFilteredTasks = (): MinifiedTask[] => {
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const tasks = useRelatedTasksTabStore((state) => state.tasks);
  const selectedTypes = useRelatedTasksTabStore((state) => state.selectedTypes);

  const filteredTasks = useMemo(() => {
    let filteredByMode = tasks;

    switch (mode) {
      case RelatedTasksMode.ContributingTasks:
        filteredByMode = tasks.filter((task) => {
          return selectedTypes.includes(task.type);
        });
        break;
      default:
        filteredByMode = tasks;
    }

    return filteredByMode;
  }, [mode, selectedTypes, tasks]);

  return filteredTasks;
};

/**
 * Hook that searches tasks based on the current search term
 *
 * @returns {MinifiedTask[]} Array of searched tasks.
 */
const useSearchedTasks = (): MinifiedTask[] => {
  const searchTerm = useRelatedTasksTabStore((state) => state.searchTerm);

  const filteredTasks = useFilteredTasks();

  const searchedTasks = useMemo(() => {
    return filterTasksBySearchTerm(filteredTasks, searchTerm);
  }, [filteredTasks, searchTerm]);

  return searchedTasks;
};

/**
 * Hook that determines if data is available to display based on the current mode and loading state
 *
 * @param {boolean} isLoading - Whether data is currently being loaded
 * @returns {boolean} True if data is available to display, false otherwise
 */
const useDataAvailable = (isLoading: boolean): boolean => {
  const tasks = useRelatedTasksTabStore((state) => state.tasks);
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedTeam = useRelatedTasksTabStore((state) => state.selectedTeam);

  if (mode === RelatedTasksMode.ContributingEpics) {
    return Boolean(selectedEpic) || tasks.length > 0;
  }

  if (mode === RelatedTasksMode.ContributingTeams) {
    return Boolean(selectedTeam) || tasks.length > 0;
  }

  return !isLoading && tasks.length > 0;
};

export { useDataAvailable, useFilteredTasks, useRelatedTasksData, useSearchedTasks };
